import React, { useRef, useState } from 'react';
import ProductTable from './ProductTable';
import ProductTiles from './ProductTiles';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import { ListViewIcon, TileViewIcon } from './components/ViewToggleIcons';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { removeProduct } from './hooks/useProducts';
import useProductPageResults from './hooks/useProductPageResults';
import { useUrlFilters } from './hooks/urlParams';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';
import { getNextSortCriterion, type FieldSortCriterion } from './utils/tableSort';
import { checkPermissionActions } from './utils/permissions';
import { getAvailableActions, getWriteActions } from './utils/permissionCatalog';

export default function ProductPage({
  organizationId,
  brandOptions,
  marketOptions = [],
  lookupsReady = true,
  refreshKey,
  onAdd,
  onEdit,
  onImportProducts,
  currentUser
}) {
    const availableActions = getAvailableActions(currentUser);
    const canImportProducts =
        checkPermissionActions(availableActions, getWriteActions('Products')) &&
        checkPermissionActions(availableActions, getWriteActions('Imports'));
    const canCreateProducts = checkPermissionActions(availableActions, getWriteActions('Products'));
    const [archiveOverlayOpen, setArchiveOverlayOpen] = useState(false);
    const [archiveOverlayProduct, setArchiveOverlayProduct] = useState<any>(null);
    const [archiveBusy, setArchiveBusy] = useState(false);
    const [actionNonce, setActionNonce] = useState(0);
    const [filters, setUrlState] = useUrlFilters<{
        marketId: number | null;
        brandId: number | null;
        active: boolean | null;
        name: string;
        view: string;
        page: number;
        pageSize: number;
    }>({
        marketId: { default: null, type: 'number' },
        brandId: { default: null, type: 'number' },
        active: {
          default: true,
          type: 'boolean',
          deserialize: (raw) => raw === 'null' || raw === 'all' ? null : raw === 'false' ? false : true,
        },
        name: { default: '', type: 'string' },
        view: { default: 'tiles', type: 'string' },
        page: {
          default: 0,
          type: 'number',
          validate: (value) => Number.isInteger(value) && Number(value) >= 0 ? value : undefined,
        },
        pageSize: {
          default: DEFAULT_PAGE_SIZE,
          type: 'number',
          validate: (value) => PAGE_SIZE_OPTIONS.includes(value as (typeof PAGE_SIZE_OPTIONS)[number]) ? value : undefined,
        },
    });
    const { view, page, pageSize } = filters;
    const setView = (nextView: string) => setUrlState((current) => ({ ...current, view: nextView }));
    const setFilters = (updater: (current: typeof filters) => typeof filters) =>
      setUrlState((current) => ({ ...updater(current), page: 0 }));
    const setPage = (nextPage: number) => setUrlState((current) => ({ ...current, page: nextPage }));
    const [sort, setSort] = useState<FieldSortCriterion>(null);

    const handleArchiveClick = (product) => {
      setArchiveOverlayProduct(product);
      setArchiveOverlayOpen(true);
    };

    const descendingFirstFields = useRef(new Set(['lastModified']));
    const [dataOrganizationId, setDataOrganizationId] = useState(organizationId);

    if (dataOrganizationId !== organizationId) {
      setDataOrganizationId(organizationId);
      setPage(0);
    }

    const resetToFirstPage = () => {
      setPage(0);
    };

    const { results: data, isLoading } = useProductPageResults({
      organizationId,
      brandOptions,
      marketOptions,
      lookupsReady,
      refreshKey,
      actionNonce,
      page,
      pageSize,
      filters: {
        marketId: filters.marketId,
        brandId: filters.brandId,
        active: filters.active,
        name: filters.name,
      },
      sort,
    });

    const toggleSortDirection = (fieldName) => {
      resetToFirstPage();
      setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
    }

  return (
    <div className="stack" data-testid="page-products">
      <FullScreenConfirmOverlay
        show={archiveOverlayOpen}
        title="Archive Product"
        message={`Are you sure you want to archive ${archiveOverlayProduct?.name || 'this product'}? This hides it from active listings.`}
        cancelLabel="Cancel"
        confirmLabel={archiveBusy ? 'Archiving…' : 'Confirm'}
        busy={archiveBusy}
        onCancel={() => {
          if (archiveBusy) return;
          setArchiveOverlayOpen(false);
          setArchiveOverlayProduct(null);
        }}
        onConfirm={async () => {
          if (archiveBusy) return;
          const productId = String(archiveOverlayProduct?.id || '');
          if (!productId) {
            setArchiveOverlayOpen(false);
            setArchiveOverlayProduct(null);
            return;
          }

          setArchiveBusy(true);
          try {
            await removeProduct(productId);
            setActionNonce((v) => v + 1);
            setArchiveOverlayOpen(false);
            setArchiveOverlayProduct(null);
            showToastSuccess('Product successfully archived.');
          } catch (error) {
            console.error('[ProductPage] Failed to archive product:', error);
            showToastError('Product could not be archived.');
          } finally {
            setArchiveBusy(false);
          }
        }}
        testId="products-archive-overlay"
      />

      <PageHeader
        title="Products"
        subtitle="Manage catalog"
      />

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              placeholder="Search products or brands"
              value={filters.name}
              onChange={(e) => {
                  resetToFirstPage();
                  setFilters((f) => ({ ...f, name: e.target.value }));
              }}
              data-testid="products-filter-search"
            />
            <select
              className="select"
              value={filters.brandId ?? 'all'}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextBrandId = raw === 'all' ? null : Number(raw);
                  resetToFirstPage();
                  setFilters((f) => ({ ...f, brandId: Number.isFinite(nextBrandId) ? nextBrandId : null }));
              }}
              style={{ width: 140, minWidth: 140 }}
              data-testid="products-filter-brand"
            >
              <option value="all">All Brands</option>
              {(brandOptions || [])
                .slice()
                .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                .map((b) => (
                <option key={String(b.id)} value={String(b.id)}>{b.name}</option>
              ))}
            </select>
            <select
              className="select"
              value={filters.marketId ?? 'all'}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextMarketId = raw === 'all' ? null : Number(raw);
                  resetToFirstPage();
                  setFilters((f) => ({ ...f, marketId: Number.isFinite(nextMarketId) ? nextMarketId : null }));
              }}
              data-testid="products-filter-market"
            >
              <option value="all">All Markets</option>
              {(marketOptions || [])
                .slice()
                .sort((a, b) => (a.name || a.code || '').localeCompare((b.name || b.code || ''), undefined, { sensitivity: 'base' }))
                .map((m) => (
                  <option key={String(m.id)} value={String(m.id)}>{m.name || m.code}</option>
              ))}
            </select>
            <select
              className="select"
              value={filters.active === null ? 'all' : String(filters.active)}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextActive = raw === 'all' ? null : (raw === 'true');
                  resetToFirstPage();
                  setFilters((f) => ({
                      ...f,
                      active: nextActive
                  }));
              }}
              data-testid="products-filter-status"
            >
              <option value="true">Active</option>
              <option value="false">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="filter-right">
            <div className="segmented view-toggle">
              <button
                type="button"
                className={`segment view-toggle-btn ${view === 'tiles' ? 'active' : ''}`}
                onClick={() => setView('tiles')}
                aria-label="Tile view"
                title="Tile view"
                data-testid="products-view-tiles"
              >
                <TileViewIcon />
              </button>
              <button
                type="button"
                className={`segment view-toggle-btn ${view === 'list' ? 'active' : ''}`}
                onClick={() => setView('list')}
                aria-label="List view"
                title="List view"
                data-testid="products-view-list"
              >
                <ListViewIcon />
              </button>
            </div>
            {canImportProducts && <button className="button cta" onClick={onImportProducts} data-testid="products-import">
              <span className="plus" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Import
            </button>}
            {canCreateProducts && <button className="button cta" onClick={onAdd} data-testid="products-create">
              <span className="plus" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              Create
            </button>}
          </div>
        </div>
      </div>

      {view === 'list' ? (
        <div className="card" data-testid="products-list-results">
            <ProductTable products={data?.data || []} brands={brandOptions || []} markets={marketOptions || []} onEdit={onEdit} onArchive={handleArchiveClick}
                sort={sort} toggleSortDirection={toggleSortDirection} currentUser={currentUser} />
        </div>
      ) : (
        <div data-testid="products-tile-results">
          <ProductTiles products={data?.data || []} brands={brandOptions || []} markets={marketOptions || []} onEdit={onEdit} onArchive={handleArchiveClick} currentUser={currentUser} />
        </div>
          )}
        <Pagination
            page={data.page}
            totalPages={Math.ceil(data.total / data.pageSize)}
            onPrev={() => { 
                if (!isLoading) {
                    setPage(data.page - 1)
                }
            }}
            onNext={() => { 
                if (!isLoading) {
                    setPage(data.page + 1)
                }
            }}
            prevLabel="◀"
            nextLabel="▶"
            prevDisabled={isLoading}
            nextDisabled={isLoading || (data.page + 1) >= Math.ceil(data.total / data.pageSize)}
            format="of"
            pageSize={data.pageSize}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            testId="products-pagination"
            onPageSizeChange={(size) => {
              setUrlState((current) => ({ ...current, pageSize: size, page: 0 }));
            }}
        />
    </div>
  );
}
